import React, { useState } from "react";
import BrandLogo from "../../components/common/BrandLogo";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Building2, Sparkles, ArrowRight, ArrowLeft, Lock, Mail,
  Users, Boxes, Megaphone, Home, Wallet, GraduationCap,
  BrainCircuit, FileText, Smartphone, LineChart,
} from "lucide-react";
import { useAuth } from "../../lib/auth";
import { useI18n } from "../../lib/i18n";

/**
 * Two-panel portal login for Fırat.
 *   1) "Site Yönetimi" — the existing CMS admin (currently live).
 *   2) "EstateOS Suite" — future mega-suite (CRM, ERP, Marketing, …).
 *
 * Users see a portal chooser first. Clicking a panel reveals that panel's
 * login form. Both panels currently authenticate against the same backend;
 * successful CMS login goes to /admin/dashboard, EstateOS goes to /estateos.
 */

const ESTATEOS_MODULES = [
  { name: "EstateOS CRM",       icon: Users },
  { name: "EstateOS ERP",       icon: Boxes },
  { name: "EstateOS Marketing", icon: Megaphone },
  { name: "EstateOS Portfolio", icon: Home },
  { name: "EstateOS Finance",   icon: Wallet },
  { name: "EstateOS Academy",   icon: GraduationCap },
  { name: "EstateOS AI",        icon: BrainCircuit },
  { name: "EstateOS Docs",      icon: FileText },
  { name: "EstateOS Mobile",    icon: Smartphone },
  { name: "EstateOS Analytics", icon: LineChart },
];

/* ============================================================
 * Portal chooser — 2 large cards visible by default
 * ============================================================ */
const PortalChooser = ({ onSelect, isAuthed, navigate }) => {
  const handleSelect = (key) => {
    // If the user is already authenticated, skip the login form and go
    // straight to the requested destination.
    if (isAuthed) {
      navigate(key === "estateos" ? "/estateos" : "/admin/dashboard", { replace: true });
      return;
    }
    onSelect(key);
  };
  return (
  <motion.div
    key="chooser"
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -16 }}
    transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
    className="w-full max-w-xl"
    data-testid="portal-chooser"
  >
    <div className="text-[0.66rem] tracking-[0.36em] uppercase text-brand-primary font-medium">
      Fırat Portal
    </div>
    <h1
      className="font-serif text-brand-navy leading-[1.05] mt-3"
      style={{ fontSize: "clamp(1.9rem, 3.4vw, 2.75rem)" }}
    >
      Hangi panele giriş yapmak istersiniz?
    </h1>
    <p className="mt-4 text-brand-medium leading-relaxed">
      Fırat ekosistemi iki ana platform üzerinden yönetilir. Devam etmek için bir panel seçin.
    </p>

    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* CMS card */}
      <button
        type="button"
        onClick={() => handleSelect("cms")}
        className="group text-left bg-white border border-black/10 hover:border-brand-navy p-6 md:p-7 transition-all hover:-translate-y-1 hover:shadow-[0_18px_50px_-24px_rgba(34,52,107,0.4)]"
        data-testid="portal-select-cms"
      >
        <div className="w-11 h-11 rounded-full bg-brand-navy text-white flex items-center justify-center">
          <Building2 size={18} strokeWidth={1.5} />
        </div>
        <div className="mt-5 text-[0.62rem] tracking-[0.28em] uppercase text-brand-primary font-medium">
          Site Yönetimi
        </div>
        <div className="mt-2 font-serif text-2xl text-brand-navy leading-tight">
          Fırat CMS
        </div>
        <p className="mt-3 text-sm text-brand-medium leading-relaxed">
          Kurumsal site içeriği, sayfalar, projeler, medya ve SEO yönetimi.
        </p>
        <div className="mt-5 inline-flex items-center gap-2 text-[0.66rem] tracking-[0.28em] uppercase text-brand-navy font-medium group-hover:gap-3 transition-all">
          Giriş <ArrowRight size={14} strokeWidth={1.5} />
        </div>
      </button>

      {/* EstateOS card */}
      <button
        type="button"
        onClick={() => handleSelect("estateos")}
        className="group text-left relative overflow-hidden p-6 md:p-7 transition-all hover:-translate-y-1 hover:shadow-[0_18px_50px_-24px_rgba(34,52,107,0.55)] border border-brand-navy"
        style={{
          background: "linear-gradient(135deg, #22346B 0%, #101F4A 100%)",
          color: "#fff",
        }}
        data-testid="portal-select-estateos"
      >
        <div className="w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center backdrop-blur-sm">
          <Sparkles size={18} strokeWidth={1.5} />
        </div>
        <div className="mt-5 text-[0.62rem] tracking-[0.28em] uppercase text-white/70 font-medium">
          EstateOS Suite
        </div>
        <div className="mt-2 font-serif text-2xl leading-tight">
          Fırat EstateOS
        </div>
        <p className="mt-3 text-sm text-white/80 leading-relaxed">
          CRM · ERP · Marketing · Portfolio · Finance · AI · Academy · Docs · Mobile · Analytics
        </p>
        <div className="mt-5 inline-flex items-center gap-2 text-[0.66rem] tracking-[0.28em] uppercase text-white font-medium group-hover:gap-3 transition-all">
          Giriş <ArrowRight size={14} strokeWidth={1.5} />
        </div>
      </button>
    </div>

    <div className="mt-10 text-sm">
      <Link
        to="/"
        className="text-brand-medium hover:text-brand-navy transition-colors inline-flex items-center gap-2"
        data-testid="portal-back-home"
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> Ana sayfaya dön
      </Link>
    </div>
  </motion.div>
  );
};

/* ============================================================
 * Login form for a specific panel
 * ============================================================ */
const PanelLogin = ({ panel, onBack }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const isEstate = panel === "estateos";
  const brand = isEstate
    ? {
        eyebrow: "EstateOS",
        title: "Fırat EstateOS'a Giriş",
        subtitle: "Portföy, CRM, ERP ve tüm entegre modüller tek platformda.",
        submitLabel: "EstateOS'a Giriş",
        target: "/estateos",
        testIdPrefix: "estateos",
        accent: "brand-navy",
      }
    : {
        eyebrow: "Site Yönetimi",
        title: "Fırat CMS'e Giriş",
        subtitle: "Kurumsal site ve içerik yönetimi paneli.",
        submitLabel: "CMS'e Giriş",
        target: "/admin/dashboard",
        testIdPrefix: "cms",
        accent: "brand-navy",
      };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      const to = location.state?.from?.pathname || brand.target;
      navigate(to, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : t("login.invalid");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.form
      key={`form-${panel}`}
      onSubmit={submit}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
      className="w-full max-w-md"
      data-testid={`${brand.testIdPrefix}-login-form`}
    >
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 text-[0.66rem] tracking-[0.28em] uppercase text-brand-medium hover:text-brand-navy transition-colors"
        data-testid={`${brand.testIdPrefix}-login-back`}
      >
        <ArrowLeft size={12} strokeWidth={1.5} /> Portala Dön
      </button>

      <div className="mt-8">
        <div className="text-[0.66rem] tracking-[0.36em] uppercase text-brand-primary font-medium">
          {brand.eyebrow}
        </div>
        <h1
          className="font-serif text-brand-navy leading-[1.05] mt-3"
          style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
        >
          {brand.title}
        </h1>
        <p className="mt-3 text-brand-medium text-sm leading-relaxed max-w-sm">
          {brand.subtitle}
        </p>
      </div>

      <div className="mt-10 space-y-6">
        <div className="relative">
          <Mail
            size={16}
            strokeWidth={1.5}
            className="absolute start-0 top-1/2 -translate-y-1/2 text-brand-medium"
          />
          <input
            type="email"
            required
            placeholder="E-posta"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-transparent border-b border-black/15 focus:border-brand-navy focus:outline-none py-4 ps-7"
            data-testid={`${brand.testIdPrefix}-login-email`}
          />
        </div>
        <div className="relative">
          <Lock
            size={16}
            strokeWidth={1.5}
            className="absolute start-0 top-1/2 -translate-y-1/2 text-brand-medium"
          />
          <input
            type="password"
            required
            placeholder="Şifre"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent border-b border-black/15 focus:border-brand-navy focus:outline-none py-4 ps-7"
            data-testid={`${brand.testIdPrefix}-login-password`}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={busy}
        className="btn-primary mt-10 w-full justify-center"
        data-testid={`${brand.testIdPrefix}-login-submit`}
      >
        {busy ? t("common.loading") : brand.submitLabel}
      </button>

      {isEstate ? (
        <div className="mt-8 border-t border-black/10 pt-6">
          <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-medium font-medium mb-3">
            EstateOS Modülleri
          </div>
          <div className="grid grid-cols-2 gap-2">
            {ESTATEOS_MODULES.map((m) => {
              const Icon = m.icon;
              return (
                <div
                  key={m.name}
                  className="flex items-center gap-2 text-xs text-brand-medium"
                >
                  <Icon size={12} strokeWidth={1.5} className="text-brand-primary" />
                  <span>{m.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </motion.form>
  );
};

/* ============================================================
 * Main page — portal chooser + form transition
 * ============================================================ */
const LoginPage = ({ initialPanel = null, hideChooser = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [panel, setPanel] = useState(initialPanel); // null | "cms" | "estateos"

  const goBack = () => {
    if (hideChooser) {
      // Dedicated login routes have no chooser to go back to → send home.
      navigate("/", { replace: true });
    } else {
      setPanel(null);
    }
  };

  return (
    <div
      className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white"
      data-testid="admin-login-page"
    >
      {/* Brand column (left) */}
      <div className="hidden lg:block relative bg-brand-navy overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1481026469463-66327c86e544"
          alt=""
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 hero-backdrop grain" />
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <BrandLogo place="header" onDark max={56} />
          <div>
            <div className="text-[0.66rem] tracking-[0.36em] uppercase text-brand-primary-bright font-medium">
              Fırat Digital Ecosystem
            </div>
            <div className="font-serif text-4xl md:text-5xl mt-4 max-w-md leading-tight">
              Kurumsal içerik ve gayrimenkul teknolojisi. Tek merkezden.
            </div>
            <p className="mt-6 text-white/70 text-sm max-w-md leading-relaxed">
              Fırat CMS ve EstateOS platformları; sitemizi, portföyümüzü, satış
              operasyonlarımızı ve akademiyi tek bir dijital ekosistem içinde
              yönetmemizi sağlar.
            </p>
          </div>
        </div>
      </div>

      {/* Right column — chooser or login */}
      <div className="flex items-center justify-center p-8 md:p-16 relative">
        <AnimatePresence mode="wait">
          {panel === null ? (
            <PortalChooser onSelect={setPanel} isAuthed={!!user} navigate={navigate} />
          ) : (
            <PanelLogin panel={panel} onBack={goBack} />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LoginPage;
